import { BehaviorSubject, combineLatest, Observable, Subject, first, map, switchMap, take, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AuthStorageService, OCC_USER_ID_CURRENT, OccEndpointsService } from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user/account/root';

import { Profil, Gender, Optin, Asset, CustomAddress } from '../models';
import { ConfigurationService } from './configuration.service';

@Injectable({
  providedIn: 'root'
})
export class MyAccountService {
  editProfilSubject = new Subject<Profil>();

  private userProfilSubject = new BehaviorSubject<Profil>(undefined);
  private rcuAccountIdSubject = new BehaviorSubject<string>(undefined);
  private isProfilLoading: boolean;

  private get config() {
    return this.configService.config;
  }

  constructor(
    private configService: ConfigurationService,
    private http: HttpClient,
    private authStorageService: AuthStorageService,
    private occEndpointsService: OccEndpointsService,
    private userAccountFacade: UserAccountFacade
  ) {}

  getGenres(): Observable<{ code: string; name: string }[]> {
    const url = this.occEndpointsService.buildUrl('titles');
    return this.http.get<Gender>(url).pipe(map((result) => result['titles']));
  }

  getOptins(): Observable<Optin[]> {
    const rcuAccountId$ = this.getRCUAccountID();
    const accessToken$ = this.authStorageService.getToken().pipe(
      map((result) => result['access_token']),
      take(1)
    );

    return combineLatest([rcuAccountId$, accessToken$]).pipe(
      switchMap(([rcuAccountId, accessToken]) => {
        const url = `${this.config.myAccount.baseUrl}/profiles/${rcuAccountId}/optins`;
        const headers = {
          Authorization: 'Bearer ' + accessToken,
          'Ocp-Apim-Subscription-Key': this.config.myAccount.subscriptionKey
        };

        return this.http.get<any>(url, { headers });
      })
    );

  }

  getReferalLink(firstName: string): Observable<{ encodedURL: string }> {
    const rcuAccountId$ = this.getRCUAccountID();

    return rcuAccountId$.pipe(
      switchMap((id) => {
        return this.http.get<{ encodedURL: string }>(`/refer_friend_link/${id}/${firstName}`);
      }));
  }

  getProfil(): Observable<Profil> {
    if (!this.isProfilLoading && this.userProfilSubject.getValue() === undefined) {
      this.isProfilLoading = true;
      const rcuAccountId$ = this.getRCUAccountID();

      const accessToken$ = this.authStorageService.getToken().pipe(
        map((result) => result['access_token']),
        take(1)
      );
      combineLatest([rcuAccountId$, accessToken$, this.configService.getConfig()])
        .pipe(
          switchMap(([rcuAccountId, accessToken, config]) => {
            const url = `${config.myAccount.baseUrl}/profiles/${rcuAccountId}`;
            const headers = {
              Authorization: 'Bearer ' + accessToken,
              'Ocp-Apim-Subscription-Key': config.myAccount.subscriptionKey
            };

            return this.http.get<Profil>(url, { headers });
          })
        )
        .subscribe((userProfil) => {
          this.isProfilLoading = false;
          this.userProfilSubject.next(userProfil);
        });
    }
    return this.userProfilSubject.asObservable().pipe(first((profile) => profile !== undefined));
  }

  getAssets(): Observable<Asset[]> {
    const rcuAccountId$ = this.getRCUAccountID();

    const accessToken$ = this.authStorageService.getToken().pipe(
      map((result) => result['access_token']),
      take(1)
    );
    return combineLatest([rcuAccountId$, accessToken$, this.configService.getConfig()]).pipe(
      switchMap(([rcuAccountId, accessToken, config]) => {
        const url = `${config.myAccount.baseUrl}/profiles/${rcuAccountId}/assets`;
        const headers = {
          Authorization: 'Bearer ' + accessToken,
          'Ocp-Apim-Subscription-Key': config.myAccount.subscriptionKey
        };

        return this.http.get<Asset[]>(url, { headers });
      })
    );
  }

  addAsset(dataProfil: Asset): Observable<Asset> {
    const rcuAccountId$ = this.getRCUAccountID();
    const accessToken$ = this.authStorageService.getToken().pipe(
      map((result) => result['access_token']),
      take(1)
    );
    return combineLatest([rcuAccountId$, accessToken$, this.configService.getConfig()]).pipe(
      switchMap(([rcuAccountId, accessToken, config]) => {
        const url = `${config.myAccount.baseUrl}/profiles/${rcuAccountId}/assets`;
        const headers = {
          Authorization: 'Bearer ' + accessToken,
          'Ocp-Apim-Subscription-Key': config.myAccount.subscriptionKey
        };
        dataProfil.accountId = rcuAccountId as string;
        dataProfil.brand = config.myAccount.brand;

        return this.http.post<Asset>(url, dataProfil, { headers });
      })
    );
  }

  deleteAsset(id: string): Observable<any> {
    const accessToken$ = this.authStorageService.getToken().pipe(
      map((result) => result['access_token']),
      take(1)
    );

    return combineLatest([accessToken$, this.configService.getConfig()]).pipe(
      switchMap(([accessToken, config]) => {
        const url = `${config.myAccount.baseUrl}/assets/${id}`;
        const headers = {
          Authorization: 'Bearer ' + accessToken,
          'Ocp-Apim-Subscription-Key': config.myAccount.subscriptionKey
        };

        return this.http.delete<any>(url, { headers });
      })
    );
  }

  deleteProfile(): void {
    this.userProfilSubject.next(undefined);
    this.rcuAccountIdSubject.next(undefined);
  }

  editProfil(dataProfil: Profil): Observable<Profil> {
    const rcuAccountId$ = this.getRCUAccountID();

    const accessToken$ = this.authStorageService.getToken().pipe(
      map((result) => result['access_token']),
      take(1)
    );
    return combineLatest([rcuAccountId$, accessToken$, this.configService.getConfig()]).pipe(
      switchMap(([rcuAccountId, accessToken, config]) => {
        const url = `${config.myAccount.baseUrl}/profiles/${rcuAccountId}`;
        const headers = {
          Authorization: 'Bearer ' + accessToken,
          'Ocp-Apim-Subscription-Key': config.myAccount.subscriptionKey
        };

        return this.http.patch<Profil>(url, dataProfil, { headers });
      }),
      tap(() => {
        this.isProfilLoading = false;
        this.userProfilSubject.next({
          ...this.userProfilSubject.getValue(),
          birthdate: dataProfil.birthdate?.toISOString()?.split('T')[0],
          householdMembers: dataProfil.householdMembers,
          firstName: dataProfil.firstName,
          lastName: dataProfil.lastName,
          gender: dataProfil.gender ? dataProfil.gender : this.userProfilSubject.getValue()?.gender
        });
        this.editProfilSubject?.next(this.userProfilSubject.getValue());
      })
    );
  }

  getAddresses(): Observable<CustomAddress[]> {
    const url = this.occEndpointsService.buildUrl('address', { urlParams: { userId: OCC_USER_ID_CURRENT } });
    return this.http.get<{ addresses: CustomAddress[] }>(url).pipe(map((result) => result['addresses']));
  }

  addAddress(address: CustomAddress): Observable<CustomAddress> {
    const url = this.occEndpointsService.buildUrl('address', { urlParams: { userId: OCC_USER_ID_CURRENT } });

    return this.http.post<CustomAddress>(url, { ...address.addressGroup, ...address.contactGroup });
  }

  editAddress(address: CustomAddress): Observable<CustomAddress> {
    const url = this.occEndpointsService.buildUrl('updateAddress', {
      urlParams: { userId: OCC_USER_ID_CURRENT, addressId: address.id }
    });
    return this.http.put<CustomAddress>(url, address.addressGroup);
  }

  deleteAddress(id: string): Observable<void> {
    const url = this.occEndpointsService.buildUrl('updateAddress', {
      urlParams: { userId: OCC_USER_ID_CURRENT, addressId: id }
    });
    return this.http.delete<void>(url, {});
  }

  setMainAddress(id: string): Observable<void> {
    const url = this.occEndpointsService.buildUrl('mainAddress', {
      urlParams: { userId: OCC_USER_ID_CURRENT, addressId: id }
    });
    return this.http.put<void>(url, {});
  }

  private getRCUAccountID(): Observable<string> {
    return this.userAccountFacade.get().pipe(
      first((user) => !!user),
      map((result) => result['rcuAccountId'])
    );
  }
}
